/*
 * assets/js/src/sticky.js — lepivé sloupce jen tehdy, když se vejdou.
 *
 * ---------------------------------------------------------------------
 * PROČ TO NENÍ JEN CSS
 * ---------------------------------------------------------------------
 * `position: sticky` u sloupce VYŠŠÍHO než okno znamená, že se jeho
 * spodek nedá dorolovat: prvek se zastaví u horní hrany a zbytek
 * zůstane pod ní. Na krátkém okně (notebook na 200 % zvětšení, telefon
 * naležato) je tak poslední widget, formulář i patička nedostupné.
 * CSS to samo poznat nemůže — výška sloupce závisí na obsahu, který se
 * mění s daty.
 *
 * VÝCHOZÍ STAV JE NELEPIVÝ. Lepivost přidává až tenhle modul třídou
 * `je-lepiva`. Kdyby se bundl nenačetl nebo spadl, zůstane sloupec
 * normálně v proudu — tedy dosažitelný. Opačné pořadí (lepivé v CSS,
 * vypínané skriptem) by z chyby JS udělalo nedostupný obsah.
 *
 * ---------------------------------------------------------------------
 * SLOUPEC VYŠŠÍ NEŽ OKNO SE UŽ NEVZDÁVÁ (majitel 17. 9. 14:0x)
 * ---------------------------------------------------------------------
 * „a pravé widgety fixovat aby nebylo vpravo prázdné místo."
 *
 * Do 17. 9. modul vysoký sloupec prostě nechal nelepivý, takže na výpisu
 * blogu odjel nahoru a vedle zbytku výpisu zůstalo prázdno. Měřeno
 * v REPORT-42: spodek sloupce byl poprvé v okně až v 19 % rolování,
 * zbylých 81 % stránky mělo pravou stranu prázdnou.
 *
 * Řešení má NÁVRH a je to jediný řádek:
 *   `top: min(88px, calc(100vh - 1459px))`
 * Vysoký sloupec dostane ZÁPORNÝ `top`, takže roluje spolu se stránkou,
 * dokud se neukáže jeho SPODEK — a teprve tam se přilepí. Původní obava
 * („spodek se nedá dorolovat") tím padá: spodek je dosažitelný vždycky,
 * jen ne hned.
 *
 * 1459 px je v návrhu výška lišty v mockupu s pevným počtem widgetů.
 * U nás se počet položek mění s obsahem, takže se měří SKUTEČNÁ výška
 * a vzorec se počítá z ní. Je to tentýž vzorec, ne nový nápad.
 */

/** Sloupce, které se chovají lepivě, když je na to místo. */
/* `.hubdes` = karta vedle odpovědi rozcestníku štítku (dev.16.17). */
/* `.svcside` = pruh „Další služby k akci" vedle textu servisní stránky (dev.16.22). */
const CILE = '.bloglayout > aside, .psum, .hubdes, .svcside';

/** Kolik místa musí zbýt pod sloupcem, aby „vejde se" znamenalo i „je vidět celý". */
const REZERVA = 24;

export function init() {
	const prvky = [...document.querySelectorAll(CILE)];
	if (!prvky.length) return 0;

	/*
	 * Zapisuje JEN ZMĚNU (dev.16.55). Dřív každé `resize` třídu odebralo
	 * a vrátilo a `top` přepsalo stejnou hodnotou. Na iPhonu mění výšku
	 * okna i lišta prohlížeče při rolování a mutace tříd v tu chvíli
	 * WebKit bere jako změnu pod prstem: první tap na odkaz zahodí
	 * (brána „změna výšky okna = 0 mutací", TH 13. 9. 2026). Třída výšku
	 * sloupce nemění (`position: sticky`), takže se měří i s ní.
	 */
	const nastav = (el, lepiva, top) => {
		if (el.classList.contains('je-lepiva') !== lepiva) el.classList.toggle('je-lepiva', lepiva);
		if (undefined === top) return;
		if (null === top) {
			if (el.style.top) el.style.removeProperty('top');
		} else if (el.style.top !== top) {
			el.style.top = top;
		}
	};

	const srovnej = () => {
		for (const el of prvky) {
			const styl = window.getComputedStyle(el);
			/*
			 * `display: contents` (tabletová vrstva výpisu blogu) nemá
			 * vlastní box: `offsetHeight` je 0 a „vejde se" by vyšlo
			 * vždycky. Lepivost na takovém prvku stejně nic nedělá.
			 */
			if ('contents' === styl.display || 'none' === styl.display) {
				nastav(el, false);
				continue;
			}
			const vyska = el.offsetHeight;
			if (!vyska) {
				nastav(el, false, null);
				continue;
			}
			const odsazeni = parseFloat(styl.getPropertyValue('--sticky-top')) || 0;
			/*
			 * Spodní rezerva per prvek (dev.16.22). Pruh služeb stojí
			 * vpravo dole přesně pod plovoucím dockem (telefon + „Nezávazně
			 * poptat akci", ~142 px nad spodní hranou okna), takže s 24 px
			 * by jeho poslední dlaždice zůstala pod dockem. Prvek bez
			 * `--sticky-rezerva` se chová jako dosud.
			 */
			const rezerva = parseFloat(styl.getPropertyValue('--sticky-rezerva')) || REZERVA;

			if (vyska + odsazeni + rezerva <= window.innerHeight) {
				/* Vejde se: drží se pod hlavičkou, `top` zůstává z CSS. */
				nastav(el, true, null);
				continue;
			}

			/*
			 * Nevejde se. `top` jde do záporu přesně o to, oč je sloupec
			 * vyšší než okno — vzorec `min(odsazeni, 100vh - výška)`
			 * z návrhu. Sloupec tedy roluje, dokud není vidět jeho
			 * spodek, a od té chvíle stojí. Nic se neusekne.
			 *
			 * Počítá se v JS, ne v CSS: `calc()` neumí přečíst výšku
			 * vlastního prvku a ta se tady mění s obsahem widgetů.
			 */
			nastav(el, true, Math.min(odsazeni, window.innerHeight - vyska - rezerva) + 'px');
		}
	};

	srovnej();
	/*
	 * Na dotykovém zařízení se změna JEN VÝŠKY okna ignoruje: je to lišta
	 * prohlížeče při rolování, ne nové rozvržení, a přepočet `top` by
	 * mutoval sloupec pod prstem (viz `nastav`). Desktop přepočítává dál
	 * při každé změně okna.
	 */
	let sirka = window.innerWidth;
	const dotyk = window.matchMedia ? window.matchMedia('(hover: none)') : null;
	window.addEventListener('resize', () => {
		const w = window.innerWidth;
		if (w === sirka && dotyk && dotyk.matches) return;
		sirka = w;
		srovnej();
	});
	/*
	 * Výška sloupce se mění i bez změny okna: obrázky ve widgetech
	 * doběhnou později, „Načíst další" přidá dlaždice. `ResizeObserver`
	 * je proto přesnější než jediné měření po načtení.
	 */
	if ('ResizeObserver' in window) {
		const ro = new ResizeObserver(srovnej);
		for (const el of prvky) ro.observe(el);
	} else {
		window.addEventListener('load', srovnej);
	}
	return prvky.length;
}
